import { categorizeCommodity, calculateRiskLevel } from './dataHelpers.js';
import { commodityConfig } from '../config/commodityConfig.js';

export function minesToGeoJSON(mines) {
  if (!mines || !Array.isArray(mines)) {
    console.warn('minesToGeoJSON received invalid data:', mines);
    return {
      type: 'FeatureCollection',
      features: []
    };
  }


  return {
    type: 'FeatureCollection',
    features: mines.map((mine) => {
      const commodityName = mine.commodity?.commodity_name || mine.commodity || 'Unknown';
      const countryName = mine.country?.country_name || mine.country_name || 'Unknown';

      const commodityInfo = categorizeCommodity(commodityName);

      let color = calculateMarkerColor(commodityName, commodityInfo.legendGroup);

      // Flatten the junction table (mine_commodity) into a unique-ordered list
      // of every commodity associated with this mine. Used by the popup to show
      // secondary/tertiary commodities under the primary tag.
      const junctionNames = Array.isArray(mine.mine_commodity)
        ? mine.mine_commodity
            .map((mc) => mc?.commodity?.commodity_name)
            .filter(Boolean)
        : [];
      const allCommodities = [];
      const seenCommodities = new Set();
      const pushCommodity = (name) => {
        if (name && !seenCommodities.has(name)) {
          seenCommodities.add(name);
          allCommodities.push(name);
        }
      };
      pushCommodity(commodityName);
      junctionNames.forEach(pushCommodity);
      const secondaryCommodities = allCommodities.slice(1);

      // Mapbox stringifies nested objects/arrays during setData, so the popup
      // can't reliably read array properties off queryRenderedFeatures. Store
      // as a comma-separated string instead — easy to split for display.
      const normalizedMine = {
        ...mine,
        commodity: commodityName,
        commodity_display: commodityInfo.display,
        commodity_category: commodityInfo.category,
        legend_group: commodityInfo.legendGroup,
        all_commodities_csv: allCommodities.join(', '),
        secondary_commodities_csv: secondaryCommodities.join(', '),
        country_name: countryName
      };

      // Flatten mine_risk_aggregate to avoid Mapbox stripping nested objects
      const flattenedProperties = {
        ...normalizedMine,
        risk_level: calculateRiskLevel(normalizedMine),
        color: color
      };

      // If mine_risk_aggregate exists, flatten it into top-level properties
      if (mine.mine_risk_aggregate && typeof mine.mine_risk_aggregate === 'object') {
        flattenedProperties.risk_overall_score = mine.mine_risk_aggregate.overall_score;
        flattenedProperties.risk_overall_score_50 = mine.mine_risk_aggregate.overall_score_50;
        flattenedProperties.risk_classification = mine.mine_risk_aggregate.risk_classification;
        flattenedProperties.risk_completeness_percentage = mine.mine_risk_aggregate.completeness_percentage;
      }

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(mine.longitude), parseFloat(mine.latitude)]
        },
        properties: flattenedProperties
      };
    })
  };
}

export function calculateMarkerColor(commodityName, legendGroup) {
  const commodityColors = commodityConfig?.COMMODITY_COLORS || {};
  const criticalColor = commodityConfig?.CRITICAL_COMMODITY_COLOR || '#FF6B6B';
  const nonCriticalColor = commodityConfig?.NON_CRITICAL_COMMODITY_COLOR || '#95A5A6';
  const defaultColor = commodityConfig?.DEFAULT_COMMODITY_COLOR || '#6B7280';

  // First check if there's a specific color for this commodity
  const commodityLower = commodityName.toLowerCase();
  const specificColor = commodityColors[commodityLower];

  if (specificColor) {
    return specificColor;
  }

  // Fall back to category colors
  if (legendGroup === 'high-value') {
    return defaultColor; // Should have had a specific color
  } else if (legendGroup === 'other') {
    return criticalColor;
  } else if (legendGroup === 'non-critical') {
    return nonCriticalColor;
  } else {
    return defaultColor;
  }
}

export function createMarkerElement(feature, options = {}) {
  const el = document.createElement('div');
  el.className = `marker marker-${feature.properties.commodity_category}`;

  const color = feature.properties.color || calculateMarkerColor(
    feature.properties.commodity,
    feature.properties.legend_group
  );

  el.style.backgroundColor = color;
  el.style.width = `${options.size || 10}px`;
  el.style.height = `${options.size || 10}px`;
  el.style.borderRadius = '50%';
  el.style.border = '1px solid white';
  el.style.cursor = 'pointer';

  if (options.showLabel) {
    const label = document.createElement('span');
    label.className = 'marker-label';
    label.textContent = feature.properties.mine_name || '';
    el.appendChild(label);
  }

  return el;
}

export function calculateBoundsForMines(mines) {
  if (!mines || mines.length === 0) {
    return null;
  }

  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  mines.forEach(mine => {
    const lng = parseFloat(mine.longitude);
    const lat = parseFloat(mine.latitude);

    if (!isNaN(lng) && !isNaN(lat)) {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }
  });

  if (minLng === Infinity || maxLng === -Infinity ||
      minLat === Infinity || maxLat === -Infinity) {
    return null;
  }

  const padding = 0.1;
  const lngPadding = (maxLng - minLng) * padding;
  const latPadding = (maxLat - minLat) * padding;

  return {
    west: minLng - lngPadding,
    east: maxLng + lngPadding,
    south: minLat - latPadding,
    north: maxLat + latPadding
  };
}

export function isPointInBounds(point, bounds) {
  if (!point || !bounds) return false;

  const lng = parseFloat(point.longitude || point[0]);
  const lat = parseFloat(point.latitude || point[1]);

  return lng >= bounds.west &&
         lng <= bounds.east &&
         lat >= bounds.south &&
         lat <= bounds.north;
}

export function calculateZoomLevel(bounds, mapContainer) {
  if (!bounds || !mapContainer) return 3;

  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 18;

  function latRad(lat) {
    const sin = Math.sin(lat * Math.PI / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx, worldPx, fraction) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI;
  const lngDiff = bounds.east - bounds.west;
  const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

  const containerRect = mapContainer.getBoundingClientRect();
  const latZoom = zoom(containerRect.height, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(containerRect.width, WORLD_DIM.width, lngFraction);

  return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

export function createClusterProperties() {
  return {
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 50,
    clusterProperties: {
      critical_count: ['+', ['case', ['==', ['get', 'commodity_category'], 'critical'], 1, 0]],
      non_critical_count: ['+', ['case', ['==', ['get', 'commodity_category'], 'non-critical'], 1, 0]]
    }
  };
}

export function getMarkerSizeExpression() {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    1.5, 1.5,
    3, 1.75,
    6, 4,
    8, 8,
    10, 15,
    12, 25,
    15, 35,
    18, 45
  ];
}

export function getMarkerStrokeExpression() {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    1, 0,
    6, 0.5,
    10, 1,
    15, 2
  ];
}

export function formatCoordinates(lng, lat, precision = 4) {
  const formattedLng = parseFloat(lng).toFixed(precision);
  const formattedLat = parseFloat(lat).toFixed(precision);

  const lngDir = lng >= 0 ? 'E' : 'W';
  const latDir = lat >= 0 ? 'N' : 'S';

  return `${Math.abs(formattedLat)}°${latDir}, ${Math.abs(formattedLng)}°${lngDir}`;
}

export function calculateDistance(point1, point2) {
  const R = 6371;
  const dLat = (point2.latitude - point1.latitude) * Math.PI / 180;
  const dLon = (point2.longitude - point1.longitude) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(point1.latitude * Math.PI / 180) * Math.cos(point2.latitude * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
}

export function findNearestMines(targetMine, allMines, count = 5) {
  if (!targetMine || !allMines || allMines.length === 0) {
    return [];
  }

  const minesWithDistance = allMines
    .filter(mine => mine.mine_id !== targetMine.mine_id)
    .map(mine => ({
      ...mine,
      distance: calculateDistance(targetMine, mine)
    }))
    .sort((a, b) => a.distance - b.distance);

  return minesWithDistance.slice(0, count);
}

export function groupMinesByCommodity(mines) {
  const groups = {};

  mines.forEach(mine => {
    const commodity = mine.commodity || 'Unknown';
    if (!groups[commodity]) {
      groups[commodity] = [];
    }
    groups[commodity].push(mine);
  });

  return groups;
}

export function createHeatmapData(mines) {
  if (!mines || mines.length === 0) {
    return {
      type: 'FeatureCollection',
      features: []
    };
  }

  return {
    type: 'FeatureCollection',
    features: mines.map(mine => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [parseFloat(mine.longitude), parseFloat(mine.latitude)]
      },
      properties: {
        weight: mine.risk_level === 'Very High' ? 1.0 :
                mine.risk_level === 'High' ? 0.8 :
                mine.risk_level === 'Medium' ? 0.6 :
                mine.risk_level === 'Low' ? 0.4 : 0.2
      }
    }))
  };
}

/**
 * Detects if the device is touch-enabled
 * Used to optimize interaction targets for mobile devices
 * @returns {boolean} True if device supports touch events
 */
export function isTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Gets touch-optimized hit area sizes for mine markers
 * Ensures minimum 44x44px touch targets per iOS HIG guidelines
 * @returns {Array} Mapbox interpolation expression for hit area radius
 */
export function getTouchHitAreaExpression() {
  // Multiplier ensures minimum 44px touch target diameter
  const minTouchTarget = 22; // 44px diameter / 2

  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    1.5, Math.max(11, minTouchTarget),
    3, Math.max(12, minTouchTarget),
    6, Math.max(16, minTouchTarget),
    8, Math.max(20, minTouchTarget),
    10, Math.max(30, minTouchTarget),
    12, Math.max(45, minTouchTarget),
    15, Math.max(60, minTouchTarget),
    18, Math.max(75, minTouchTarget)
  ];
}